import { zodResolver } from "@hookform/resolvers/zod";
import { Print } from "@mui/icons-material";
import { Card, Grid } from "@mui/material";
import {
  UpdateWorkOrderInputSchema,
  cleanUpdateWorkOrderInput,
  getDefaultValues,
  schema,
} from "DDD/action-objects/WorkOrderItemsUpdate";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { JobPhase, JobPhaseFile, ProposalProduct } from "generated/graphql";
import { CustomJobPhase } from "hooks/jobs/job-phases/useGetJobPhase";
import useGetJobPhaseCostItem from "hooks/jobs/job-phases/useGetJobPhaseCostItem";
import useUpdateWorkOrderItems from "hooks/jobs/job-phases/useUpdateWorkOrderItems";
import useProposalProductDetails from "hooks/products/useProposalProductDetails";
import { JobPhaseContext } from "modules/JobPhases/context/JobPhaseContext";
import JobDetails from "modules/jobs/WorkOrder/JobDetails";
import JobImages from "modules/jobs/WorkOrder/JobImages";
import JobNotes from "modules/jobs/WorkOrder/JobNotes";
import JobOverviewSection from "modules/jobs/WorkOrder/JobOverviewSection";
import JobPhaseScheduleSection from "modules/jobs/WorkOrder/JobPhaseScheduleSection";
import OrganizationAndForemanDetails from "modules/jobs/WorkOrder/OrganizationAndForemanDetails";
import ProductDetails from "modules/jobs/WorkOrder/ProductDetails";
import { ReactNode, useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

interface Props {
  data?: JobPhase;
  isJob?: boolean;
  isModal?: boolean;
  children?: ReactNode;
}

export default function WorkOrderComponent({
  data,
  children,
  isJob = false,
  isModal = false,
}: Props) {
  const tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
  const today = new Date(Date.now() - tzoffset).toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const jobPhaseId = data?.id;
  const externalJobPhaseId = data?.externalId;
  const { data: jobPhaseCostItem } = useGetJobPhaseCostItem({
    date,
    jobPhaseId: data?.id,
  });

  const [updateWorkOrderItems] = useUpdateWorkOrderItems();
  const methods = useForm<UpdateWorkOrderInputSchema>({
    defaultValues: getDefaultValues({ jobPhase: data as CustomJobPhase }),
    resolver: zodResolver(schema),
  });
  const onWorkOrderSubmit = useCallback(async (values: UpdateWorkOrderInputSchema) => {
    const cleanedValues = cleanUpdateWorkOrderInput(values);
    await updateWorkOrderItems({
      variables: {
        input: {
          ...cleanedValues,
        },
      },
    });
  }, []);

  return (
    <JobPhaseContext.Provider value={{ jobPhase: data as CustomJobPhase, jobPhaseCostItem, date }}>
      <FormProvider {...methods}>
        <style>
          {`
            @media print {
              @page {
                margin: 0.2in;
              }

              html, body {
                margin: 0 !important;
                padding: 0 !important;
              }
            }
          `}
        </style>
        <MDBox
          component="form"
          onSubmit={methods.handleSubmit(onWorkOrderSubmit)}
          id="work-order-print"
          sx={{
            "@media print": {
              fontSize: "60% !important",
              lineHeight: "1.2 !important",
              "& .MuiPaper-root, & .MuiCard-root": {
                borderRadius: "0 !important",
                overflow: "visible !important",
                boxShadow: "none !important",
              },
              "& .MuiTableContainer-root": {
                borderRadius: "0 !important",
                overflow: "visible !important",
              },
              "& .print-images-page": {
                display: "block !important",
                breakBefore: "page",
                pageBreakBefore: "always",
                marginTop: "12px",
              },
              "& .screen-images-panel": {
                display: "none !important",
              },
              "& *": {
                fontSize: "inherit !important",
                lineHeight: "inherit !important",
              },
            },
            "@media screen": {
              "& .print-images-page": {
                display: "none",
              },
            },
          }}
        >
          <MDBox
            bgColor="#f5f5f5"
            py={1.5}
            px={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              "@media print": {
                padding: "8px 10px",
              },
            }}
          >
            <MDTypography variant="h4">{`${data?.proposalStage?.name} - ${data?.organizationProductTypeName} - Work Order #${externalJobPhaseId}`}</MDTypography>
            <MDBox display="flex" justifyContent="flex-end" className="no-print">
              {!isJob && !isModal && (
                <MDButton variant="contained" color="blue" onClick={() => window.print()}>
                  <Print />
                </MDButton>
              )}
              {isModal && (
                <MDButton
                  variant="contained"
                  color="blue"
                  onClick={() => {
                    window.open(`/jobs/${data?.job?.id}/phases/${jobPhaseId}/work-order`, "_blank");
                  }}
                >
                  <Print />
                </MDButton>
              )}
            </MDBox>
          </MDBox>

          {children}
          {data && (
            <>
              <MDBox
                my={2}
                sx={{
                  "@media print": {
                    margin: 0,
                    paddingTop: 1,
                    paddingBottom: 1,
                  },
                }}
              >
                <Card>
                  <MDBox
                    p={1.5}
                    sx={{
                      "@media print": {
                        paddingLeft: 0,
                        paddingRight: 0,
                        paddingTop: 1,
                        paddingBottom: 1,
                      },
                    }}
                  >
                    <Grid container spacing={2} id="work-order-header" alignItems="stretch">
                      <Grid item xs={6}>
                        <ProductDetails phase={data as JobPhase} />
                      </Grid>
                      <Grid item xs={6}>
                        <OrganizationAndForemanDetails
                          organization={data?.organization}
                          contact={data?.job?.contact}
                          foreman={data?.foremanUser}
                          estimator={data?.proposal?.user}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <JobPhaseScheduleSection data={data as JobPhase} />
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </MDBox>

              <JobNotes notes={data?.notes} jobPhaseId={data?.id} />
              <JobDetails
                jobPhase={data as JobPhase}
                sideContent={
                  <MDBox className="screen-images-panel">
                    <JobImages jobImages={data?.files as JobPhaseFile[]} />
                  </MDBox>
                }
              />
              <JobOverviewSection jobId={data?.job?.id} jobPhaseId={data?.id} isJob={isJob} />
              <MDBox className="print-images-page">
                <JobImages jobImages={data?.files as JobPhaseFile[]} />
              </MDBox>
            </>
          )}
          <MDBox p={2} display="flex" justifyContent="flex-end" alignItems="flex-end">
            <MDButton type="submit" color="success" className="no-print">
              Save Work Order
            </MDButton>
          </MDBox>
        </MDBox>
      </FormProvider>
    </JobPhaseContext.Provider>
  );
}
